import { useState, type ButtonHTMLAttributes, type ReactElement, type ReactNode } from "react";

import { Button } from "../components/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/dialog";

export interface MediaPreviewDialogLabels {
  /** Titel van het venster, meestal de bestandsnaam. */
  title: string;
  /** Alternatieve tekst bij de afbeelding. */
  imageAlt: string;
  /** Tekst wanneer er geen beeld te tonen valt. */
  placeholder: string;
  /** Tekst zolang het beeld wordt opgehaald. */
  loading: string;
  close: string;
  download?: string;
}

export interface MediaPreviewDialogProps {
  /** Wat het venster opent — meestal de miniatuur, als knop. */
  trigger: ReactElement<ButtonHTMLAttributes<HTMLButtonElement>>;
  labels: MediaPreviewDialogLabels;
  /**
   * Haalt het adres van de grote versie op. Wordt pas aangeroepen wanneer het venster opengaat:
   * een lijst van honderd bestanden mag geen honderd grote afbeeldingen ophalen.
   */
  loadImageSrc: () => Promise<string | null>;
  /** De gegevens naast het beeld — product, merk, type, datum. */
  details?: ReactNode;
  onDownload?: () => void;
}

/**
 * Eén bestand groot bekijken, in een venster.
 *
 * Losgetrokken uit {@link MediaGallery}, waar hetzelfde venster al zat maar vastzat aan een hele
 * galerij met eigen groepering, eigen tegels en acht verplichte labels. De mediabibliotheek heeft
 * alleen dit stuk nodig, en er hoort er maar één van te bestaan — twee zouden uit elkaar gaan
 * lopen.
 *
 * Anders dan in de galerij staan de gegevens en de downloadknop hier wél ín het venster.
 */
export function MediaPreviewDialog({
  trigger,
  labels,
  loadImageSrc,
  details,
  onDownload,
}: MediaPreviewDialogProps) {
  const [src, setSrc] = useState<string | null>(null);
  const [bezig, setBezig] = useState(false);
  const [mislukt, setMislukt] = useState(false);

  async function handleOpenChange(open: boolean) {
    if (!open || src != null || bezig) return;
    setBezig(true);
    setMislukt(false);
    try {
      const gevonden = await loadImageSrc();
      if (gevonden == null) setMislukt(true);
      else setSrc(gevonden);
    } catch {
      // Een mislukte oplossing maakt het venster niet onbruikbaar; de gegevens en de downloadknop
      // blijven staan.
      setMislukt(true);
    } finally {
      setBezig(false);
    }
  }

  return (
    <Dialog onOpenChange={(open) => void handleOpenChange(open)}>
      <DialogTrigger>{trigger}</DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{labels.title}</DialogTitle>
        </DialogHeader>

        {!mislukt && src != null ? (
          <img
            alt={labels.imageAlt}
            // Zestig in plaats van zeventig: titel, gegevens, knoppen en de marges vragen samen
            // ruwweg een kwart van een laag scherm. Op zeventig ging het totaal over de
            // schermhoogte heen en stak de bovenkant van het venster erboven uit.
            className="max-h-[60vh] w-full object-contain"
            data-testid="media-preview-image"
            onError={() => setMislukt(true)}
            src={src}
          />
        ) : (
          <div
            className="flex h-48 items-center justify-center rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--background-muted)] text-sm text-[var(--foreground-muted)]"
            data-testid="media-preview-placeholder"
          >
            {bezig ? labels.loading : labels.placeholder}
          </div>
        )}

        {details != null && (
          <div className="text-sm text-[var(--foreground-muted)]">
            {details}
          </div>
        )}

        <div className="flex items-center gap-2">
          {onDownload != null && labels.download != null && (
            <Button onClick={onDownload} size="sm" variant="secondary">
              {labels.download}
            </Button>
          )}
          <DialogClose>
            <Button size="sm" variant="outline">
              {labels.close}
            </Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}
